import { Button, styled } from '@mui/material';
import _ from 'lodash';
import React from 'react';

interface Props {
  text: React.ReactNode;
  border?: 'rect' | 'rounded' | 'round';
  size?: 'extra-small' | 'small' | 'normal' | 'medium' | 'large';
  color?: 'primary' | 'error' | 'success' | 'secondary';
  noLineBreak?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  style?: React.CSSProperties;
  /** @deprecated Legacy prop – kept for backward compat */
  success?: boolean | null;

  action?(event: any | undefined): void;
}

interface StyledButtonProps {
  ownerState: {
    border: 'rect' | 'rounded' | 'round';
    size: 'extra-small' | 'small' | 'medium' | 'large';
    color: 'primary' | 'error' | 'success' | 'secondary';
    noLineBreak?: boolean;
  };
}

const getGradient = (color: string, theme: any, reverse = false) => {
  const gradients: Record<string, [string, string]> = {
    primary: [theme.palette.primary.main, theme.palette.secondary.main],
    secondary: [theme.palette.warning.main, theme.palette.warning.light],
    error: [theme.palette.error.main, theme.palette.error.light],
    success: [theme.palette.success.main, theme.palette.success.light],
  };
  const [a, b] = gradients[color] || gradients.primary;
  return reverse
    ? `linear-gradient(to right, ${b}, ${a})`
    : `linear-gradient(to right, ${a}, ${b})`;
};

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<StyledButtonProps>(({ theme, ownerState }) => ({
  height: 'fit-content',
  position: 'relative',
  whiteSpace: ownerState.noLineBreak ? 'nowrap' : undefined,
  borderRadius:
    ownerState.border === 'rounded' ? '6px' : ownerState.border === 'round' ? '16px' : 0,
  background: getGradient(ownerState.color, theme),
  textTransform: 'none',
  fontSize:
    ownerState.size === 'extra-small'
      ? theme.typography.pxToRem(13)
      : ownerState.size === 'large'
      ? theme.typography.pxToRem(18)
      : theme.typography.pxToRem(15),
  fontWeight:
    ownerState.size === 'extra-small' || ownerState.size === 'small' ? 'lighter' : 'bold',
  color: '#FFF',
  lineHeight: 1,
  padding:
    ownerState.size === 'extra-small'
      ? '5.5px 8px'
      : ownerState.size === 'small'
      ? '6.5px 12px'
      : ownerState.size === 'medium'
      ? '12px 48px'
      : ownerState.size === 'large'
      ? '16px 64px'
      : undefined,
  '&:hover': {
    background: getGradient(ownerState.color, theme, true),
  },
  '&.Mui-disabled': {
    background: theme.palette.action.disabledBackground,
    '& svg': {
      color: theme.palette.action.disabled,
    },
  },
  '& svg': {
    fontSize:
      ownerState.size === 'small'
        ? '16px !important'
        : ownerState.size === 'medium'
        ? '20px !important'
        : ownerState.size === 'large'
        ? '24px !important'
        : undefined,
    color: '#FFF',
  },
}));

const PaiButton: React.FC<Props> = ({
  text,
  border = 'rect',
  size = 'medium',
  color = 'primary',
  noLineBreak,
  fullWidth,
  loading,
  startIcon,
  endIcon,
  disabled,
  style,
  action,
}) => {
  // Map legacy 'normal' size to 'medium'
  const resolvedSize = size === 'normal' ? 'medium' : size;

  return (
    <StyledButton
      ownerState={{ border, size: resolvedSize, color, noLineBreak }}
      onClick={action}
      variant={'contained'}
      fullWidth={fullWidth}
      startIcon={startIcon}
      endIcon={endIcon}
      loading={loading}
      disabled={disabled}
      disableElevation={true}
      style={style}
    >
      {typeof text === 'string' ? _.upperFirst(text) : text}
    </StyledButton>
  );
};

export default PaiButton;
